/**
 * Application Routes
 * Defines all routes for the admin application
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  AgentListPage,
  AgentConfigPage,
  LoginPage,
  MonitoringPage,
  SkillsPage,
  SystemSettingsPage,
  ToolManagementPage,
  BusinessTemplatesPage,
  AuditPage,
  EvaluationPage,
  PlaygroundPage,
  ImageWorkflowsPage,
  SchedulesPage,
  ReportsPage,
  PromptTemplatesPage,
  UsersPage,
  AnnotationBatchesPage,
  AnnotationExportPage,
  AnnotationWorkbenchPage,
  AnnotationStatsPage,
  ClassificationAnalyticsPage,
  MessageFeedbackPage,
  FeatureFlagsPage,
  SubscriptionSettingsPage,
  NotificationTemplatesPage,
  ContentPacksPage,
  TracesPage,
} from '@/pages';

// Check if user is authenticated
function isAuthenticated(): boolean {
  const token = localStorage.getItem('admin_token');
  return !!token;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  if (!isAuthenticated()) {
    // Save the attempted path for redirect after login
    sessionStorage.setItem('admin_redirect_after_login', location.pathname);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Login page - public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Agent list - protected home page */}
      <Route path="/" element={
        <ProtectedRoute>
          <AgentListPage />
        </ProtectedRoute>
      } />

      {/* Agent configuration editor - protected */}
      <Route path="/agents/:agentType" element={
        <ProtectedRoute>
          <AgentConfigPage />
        </ProtectedRoute>
      } />

      {/* System Settings - protected */}
      <Route path="/system-settings" element={
        <ProtectedRoute>
          <SystemSettingsPage />
        </ProtectedRoute>
      } />

      {/* Tool Management - protected */}
      <Route path="/tool-management" element={
        <ProtectedRoute>
          <ToolManagementPage />
        </ProtectedRoute>
      } />

      {/* Business Templates - protected */}
      <Route path="/business-templates" element={
        <ProtectedRoute>
          <BusinessTemplatesPage />
        </ProtectedRoute>
      } />

      {/* Skills Management - protected */}
      <Route path="/skills" element={
        <ProtectedRoute>
          <SkillsPage />
        </ProtectedRoute>
      } />

      {/* Service Monitoring - protected */}
      <Route path="/monitoring" element={
        <ProtectedRoute>
          <MonitoringPage />
        </ProtectedRoute>
      } />

      {/* Push Test - redirect to notification-templates */}
      <Route path="/push-test" element={<Navigate to="/notification-templates" replace />} />

      {/* Notification Templates - protected */}
      <Route path="/notification-templates" element={
        <ProtectedRoute>
          <NotificationTemplatesPage />
        </ProtectedRoute>
      } />

      {/* Evaluation Center - consolidated tab page */}
      <Route path="/evaluation" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />
      <Route path="/evaluation/:tab" element={
        <ProtectedRoute>
          <EvaluationPage />
        </ProtectedRoute>
      } />

      {/* Legacy redirects for old evaluation URLs */}
      <Route path="/eval-scenarios" element={<Navigate to="/evaluation/scenarios" replace />} />
      <Route path="/eval-metrics" element={<Navigate to="/evaluation/metrics" replace />} />
      <Route path="/test-suites" element={<Navigate to="/evaluation/suites" replace />} />

      {/* Playground - protected */}
      <Route path="/playground" element={
        <ProtectedRoute>
          <PlaygroundPage />
        </ProtectedRoute>
      } />

      {/* Image Workflows - protected */}
      <Route path="/image-workflows" element={
        <ProtectedRoute>
          <ImageWorkflowsPage />
        </ProtectedRoute>
      } />

      {/* Unified Schedules (Temporal-backed) - protected */}
      <Route path="/schedules" element={
        <ProtectedRoute>
          <SchedulesPage />
        </ProtectedRoute>
      } />

      {/* Legacy redirect: old workflow-schedules → unified schedules */}
      <Route path="/workflow-schedules" element={<Navigate to="/schedules" replace />} />

      {/* Content Packs - protected */}
      <Route path="/content-packs" element={
        <ProtectedRoute>
          <ContentPacksPage />
        </ProtectedRoute>
      } />

      {/* Reports Dashboard - protected */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/reports/:tab" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />

      {/* Prompt Templates - protected */}
      <Route path="/prompt-templates" element={
        <ProtectedRoute>
          <PromptTemplatesPage />
        </ProtectedRoute>
      } />

      {/* User Management - protected */}
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />

      {/* Classification Analytics - protected */}
      <Route path="/classification-analytics" element={
        <ProtectedRoute>
          <ClassificationAnalyticsPage />
        </ProtectedRoute>
      } />

      {/* Message Feedback - protected */}
      <Route path="/message-feedback" element={
        <ProtectedRoute>
          <MessageFeedbackPage />
        </ProtectedRoute>
      } />

      {/* Feature Flags - protected */}
      <Route path="/feature-flags" element={
        <ProtectedRoute>
          <FeatureFlagsPage />
        </ProtectedRoute>
      } />

      {/* Audit - protected */}
      <Route path="/audit" element={
        <ProtectedRoute>
          <AuditPage />
        </ProtectedRoute>
      } />

      {/* Subscription Settings - protected */}
      <Route path="/subscription-settings/:tab?" element={
        <ProtectedRoute>
          <SubscriptionSettingsPage />
        </ProtectedRoute>
      } />

      {/* Annotation System - protected */}
      <Route path="/annotation/batches" element={
        <ProtectedRoute>
          <AnnotationBatchesPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/export" element={
        <ProtectedRoute>
          <AnnotationExportPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/workbench" element={
        <ProtectedRoute>
          <AnnotationWorkbenchPage />
        </ProtectedRoute>
      } />
      <Route path="/annotation/stats" element={
        <ProtectedRoute>
          <AnnotationStatsPage />
        </ProtectedRoute>
      } />

      {/* Traces - protected */}
      <Route path="/traces" element={
        <ProtectedRoute>
          <TracesPage />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
